// src/components/TeachersPage.jsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// Import data directly (consider lifting state in real app)
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal'; // Keep modal for Adding
import { v4 as uuidv4 } from 'uuid'; // For generating part of the code

// === MODIFICATION START: Removed TrashIcon, moved to detail page ===
// === END ICON MODIFICATION ===

function TeachersPage({ teachers, onAddTeacher }) {
  const navigate = useNavigate();
  const [filterText, setFilterText] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newTeacherData, setNewTeacherData] = useState({ name: '', email: '' });

  const filteredTeachers = useMemo(() => {
    const sourceTeachers = teachers || [];
    const query = filterText.trim().toLowerCase();
    if (!query) return sourceTeachers;

    // Split by whitespace to support versatile multi-term searching.
    const terms = query.split(/\s+/).filter(Boolean);

    return sourceTeachers.filter((teacher) => {
      const searchableParts = [
        teacher.name || '',
        teacher.email || '',
        ...(Array.isArray(teacher.tags) ? teacher.tags : [])
      ];
      const searchableText = searchableParts.join(' ').toLowerCase();

      // Every term must be present somewhere in name/email/tags.
      return terms.every(term => searchableText.includes(term));
    });
  }, [teachers, filterText]);

  // --- Add Teacher Modal Logic ---
  const openAddModal = () => {
    setNewTeacherData({ name: '', email: '' }); // Reset form
    setIsAddModalOpen(true);
  };
  const closeAddModal = () => setIsAddModalOpen(false);

  const handleAddTeacherInputChange = (e) => {
    const { name, value } = e.target;
    setNewTeacherData(prev => ({ ...prev, [name]: value }));
  };

  // Helper function to generate teacher code from name
  const generateTeacherCode = (name) => {
    if (!name) return `teacher-${uuidv4().substring(0, 4)}`; // Fallback if name is empty
    const baseCode = name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, '');   // Trim leading/trailing hyphens
    // Limit length and add random suffix for uniqueness
    const shortSuffix = uuidv4().substring(0, 4);
    return `${baseCode.substring(0, 15)}-${shortSuffix}`; // Example: limit base name part length
  };

  const handleSaveNewTeacher = (e) => {
    e.preventDefault();
    if (!newTeacherData.name || !newTeacherData.email) {
      alert('Name and email are required.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(newTeacherData.email)) {
      alert('Please enter a valid email address.');
      return;
    }

    // Generate the teacher code automatically
    const generatedCode = generateTeacherCode(newTeacherData.name.trim());

    // Prepare data for the handler, including the generated code
    const dataToSend = {
      name: newTeacherData.name.trim(),
      email: newTeacherData.email.trim(),
      teacher_code: generatedCode
    };

    // Call the handler passed from App.jsx
    if (typeof onAddTeacher === 'function') {
      console.log("Sending teacher data to handler:", dataToSend);
      onAddTeacher(dataToSend); // Pass { name, email, teacher_code }
      closeAddModal();
    } else {
      console.warn("onAddTeacher function not provided.");
      alert("Add functionality requires parent state management.");
    }
  };
  // --- End Add Teacher Modal Logic ---

  return (
    <div> {/* Removed redundant padding, handled by SetupPage */}
      {/* Filter and Add Button Bar */}
      <Card className="mb-6 p-4">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="w-full sm:w-auto sm:flex-1">
            <Input
              type="text"
              placeholder="Filter teachers by name, email, or tags..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full"
            />
          </div>
          <Button
            variant="primary"
            className="text-sm py-2 px-4 flex items-center space-x-1 w-full sm:w-auto"
            onClick={openAddModal} // Open the add modal
          >
            <span className="text-lg font-bold leading-none mr-1">+</span>
            <span>Add Teacher</span>
          </Button>
        </div>
      </Card>

      {/* Teachers Table Card */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTeachers.length > 0 ? (
                  filteredTeachers.map((teacher) => (
                    <tr
                      key={teacher.id}
                      onClick={() => navigate(`/setup/teachers/${teacher.id}`)}
                      className="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{teacher.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center gap-2 flex-wrap">
                          {Array.isArray(teacher.tags) && teacher.tags.length > 0 ? (
                            teacher.tags.map(tag => (
                              <span
                                key={`${teacher.id}-${tag}`}
                                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                title={tag}
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400 italic">No tags</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center text-gray-500 py-4">
                      {filterText ? 'No teachers match your filter.' : 'No teachers defined yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Add Teacher Modal */}
      <Modal isOpen={isAddModalOpen} onClose={closeAddModal} title="Add New Teacher">
        <form onSubmit={handleSaveNewTeacher} className="space-y-4">
          <div>
            <label htmlFor="add-teacher-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <Input
              id="add-teacher-name"
              name="name"
              value={newTeacherData.name}
              onChange={handleAddTeacherInputChange}
              placeholder="Teacher's full name"
              required
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="add-teacher-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input
              id="add-teacher-email"
              name="email"
              type="email"
              value={newTeacherData.email}
              onChange={handleAddTeacherInputChange}
              placeholder="teacher@example.com"
              required
            />
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={closeAddModal}>Cancel</Button>
            <Button type="submit" variant="primary">Add Teacher</Button>
          </div>
        </form>
      </Modal>

    </div>
  );
}

export default TeachersPage;